import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';
import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
    message: string;
    type: ToastType;
    isVisible: boolean;
    onClose: () => void;

    duration?: number;
    action?: {
        label: string;
        onClick: () => void;
    };
}

export const Toast: React.FC<ToastProps> = ({ 
    message, 
    type, 
    isVisible, 
    onClose, 
    duration = 5000,
    action
}) => {
    useEffect(() => {
        if (isVisible) {
            const timer = setTimeout(() => {
                onClose();
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [isVisible, duration, onClose]);

    if (!isVisible) return null;

    const getStyles = () => {
        switch (type) {
            case 'success':
                return 'bg-green-50 border-green-200 text-green-800';
            case 'error':
                return 'bg-red-50 border-brand-red text-brand-red';
            case 'info':
            default:
                return 'bg-blue-50 border-blue-200 text-blue-800';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle size={20} className="shrink-0" />;
            case 'error':
                return <AlertCircle size={20} className="shrink-0" />;
            case 'info':
            default:
                return <Info size={20} className="shrink-0" />;
        }
    };

    return (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-[100] w-full max-w-md px-4 pointer-events-none animate-in slide-in-from-top-4 fade-in duration-300">
            <div className={`pointer-events-auto flex items-start gap-3 p-4 rounded-xl border shadow-lg ${getStyles()}`}>
                {getIcon()}
                <div className="flex-1 text-sm font-medium pt-0.5">
                    {message}
                </div>
                {action && (
                    <button
                        onClick={action.onClick}
                        className={`text-sm font-semibold hover:underline shrink-0 ${
                            type === 'error' ? 'text-brand-red' : 
                            type === 'success' ? 'text-green-700' : 'text-blue-700'
                        }`}
                    >
                        {action.label}
                    </button>
                )}
                <button 
                    onClick={onClose}
                    className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                >
                    <X size={18} />
                </button>
            </div>
        </div>
    );
};
